export const NARRATION_CREATE_DRAFT_KEY = 'twin_tales_narration_create_draft_v1';
export const NARRATION_CREATE_DRAFT_VERSION = 1;

/** URLs that survive a full page reload (not blob: or data:). */
export function isPersistableMediaUrl(url) {
  if (!url || typeof url !== 'string') return false;
  if (url.startsWith('blob:') || url.startsWith('data:')) return false;
  return url.startsWith('http') || url.startsWith('/') || url.startsWith('//');
}

export function sanitizeSceneForDraft(scene) {
  if (!scene || typeof scene !== 'object') return null;
  const image_url = isPersistableMediaUrl(scene.image_url) ? scene.image_url : null;
  const source_image_url = isPersistableMediaUrl(scene.source_image_url)
    ? scene.source_image_url
    : image_url;
  const storyboard_sheet_url = isPersistableMediaUrl(scene.storyboard_sheet_url)
    ? scene.storyboard_sheet_url
    : isPersistableMediaUrl(scene.composite_source_url)
      ? scene.composite_source_url
      : null;
  const panel_source_image_url = isPersistableMediaUrl(scene.panel_source_image_url)
    ? scene.panel_source_image_url
    : null;
  const audio_url = isPersistableMediaUrl(scene.audio_url) ? scene.audio_url : null;
  return {
    ...scene,
    image_url,
    source_image_url,
    storyboard_sheet_url,
    panel_source_image_url,
    audio_url,
  };
}

export function buildNarrationCreateDraftSnapshot(state) {
  return {
    v: NARRATION_CREATE_DRAFT_VERSION,
    savedAt: new Date().toISOString(),
    form: state.form,
    wizardStep: state.wizardStep,
    imageStoryEntryMode: state.imageStoryEntryMode,
    imageStoryScenes: (state.imageStoryScenes || [])
      .map(sanitizeSceneForDraft)
      .filter(Boolean),
    imageStoryPreviewUrl: isPersistableMediaUrl(state.imageStoryPreviewUrl)
      ? state.imageStoryPreviewUrl
      : null,
    imageStoryDetectionMode: state.imageStoryDetectionMode || '',
    imageStoryDetectedBoxes: Array.isArray(state.imageStoryDetectedBoxes)
      ? state.imageStoryDetectedBoxes
      : [],
    trimSeparateSegment: state.trimSeparateSegment || '',
    aiPlotText: state.aiPlotText || '',
    aiPlotPanelCount: state.aiPlotPanelCount,
    aiPlotLayout: state.aiPlotLayout,
    aiPlotVisualStyle: state.aiPlotVisualStyle,
    imageStoryGenJobId: state.imageStoryGenJobId || null,
    imageStorySourceJobId: state.imageStorySourceJobId || null,
    imageStoryGenAppend: Boolean(state.imageStoryGenAppend),
  };
}

export function parseNarrationCreateDraft(raw) {
  try {
    const d = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!d || d.v !== NARRATION_CREATE_DRAFT_VERSION || !d.form || typeof d.form !== 'object') {
      return null;
    }
    return d;
  } catch {
    return null;
  }
}

export function readNarrationCreateDraft() {
  try {
    const raw = localStorage.getItem(NARRATION_CREATE_DRAFT_KEY);
    if (!raw) return null;
    return parseNarrationCreateDraft(raw);
  } catch {
    return null;
  }
}

export function writeNarrationCreateDraft(snapshot) {
  try {
    localStorage.setItem(NARRATION_CREATE_DRAFT_KEY, JSON.stringify(snapshot));
    return true;
  } catch {
    return false;
  }
}

export function removeNarrationCreateDraft() {
  try {
    localStorage.removeItem(NARRATION_CREATE_DRAFT_KEY);
  } catch {
    /* ignore */
  }
}

export function draftHasMeaningfulContent(draft) {
  if (!draft) return false;
  const f = draft.form || {};
  if (String(f.title || '').trim()) return true;
  if (String(f.topic || '').trim()) return true;
  if (String(f.full_script || '').trim()) return true;
  if (Array.isArray(f.source_segments) && f.source_segments.length > 0) return true;
  if (Array.isArray(draft.imageStoryScenes) && draft.imageStoryScenes.length > 0) return true;
  if (draft.imageStoryPreviewUrl) return true;
  if (String(draft.aiPlotText || '').trim()) return true;
  if (draft.imageStoryGenJobId) return true;
  if (draft.imageStorySourceJobId) return true;
  return false;
}

export function formatDraftSavedLabel(savedAt) {
  if (!savedAt) return 'saved draft on this device';
  const t = new Date(savedAt);
  if (Number.isNaN(t.getTime())) return 'saved draft on this device';
  return `draft saved ${t.toLocaleString()} on this device`;
}
